<template>
  <div class="blog-home-container">
    <!-- Blog Main Page -->
    <section v-if="error" class="error">Error: {{ error }}</section>
    <section v-else class="blog-layout">
      <div class="blog-main articles-parent">
        <!-- Featured Article -->
        <div v-if="featuredArticles?.[0]" class="article-card featured">
          <a :href="`/pulse/articles/${featuredArticles[0].id}-${featuredArticles[0].slug}`" class="article-image">
            <img
              :src="featuredArticles[0].imageSizes['regular']?.url ?? featuredArticles[0].image"
              :alt="featuredArticles[0].title"
              class="article-image"
            >
          </a>
          <div class="article-content">
            <div class="article-meta">
              <a
                v-if="featuredArticles[0].category"
                :href="`/pulse/categories/${featuredArticles[0].categorySlug}`"
                class="s-badge s-badge-dark category-link"
              >
                {{ featuredArticles[0].category }}
              </a>
              <span class="date">{{ formatDate(featuredArticles[0].date) }}</span>
            </div>
            <h1 class="article-title">
              <a :href="`/pulse/articles/${featuredArticles[0].id}-${featuredArticles[0].slug}`">
                {{ featuredArticles[0].title }}
              </a>
            </h1>
            <p class="article-description">{{ featuredArticles[0].preview }}</p>
          </div>
        </div>

        <!-- Article Grid -->
        <div class="article-grid">
          <div
            v-for="article in articles"
            :key="article.id"
            class="article-card"
          >
            <a :href="`/pulse/articles/${article.id}-${article.slug}`" class="article-image-link">
              <img
                :src="article.imageSizes['thumbnail']?.url"
                :alt="article.title"
                class="article-image"
              >
            </a>

            <div class="article-content">
              <div class="article-meta">
                <a
                  v-if="article.category"
                  :href="`/pulse/categories/${article.categorySlug}`"
                  class="s-badge s-badge-dark category-link"
                >
                  {{ article.category }}
                </a>
                <span class="date">{{ formatDate(article.date) }}</span>
              </div>

              <h2 class="article-title">
                <a :href="`/pulse/articles/${article.id}-${article.slug}`">
                  {{ article.title }}
                </a>
              </h2>

              <p class="article-description">{{ article.preview }}</p>
            </div>
          </div>
        </div>

        <!-- Load More Trigger -->
        <div v-if="hasMorePosts" ref="loadMoreTrigger" class="read-more">
          <s-button
            class="button-primary-dark s-button-hover-rise"
            @click="() => loadMore(false)"
            :loading="loading"
          >
            {{ loading ? 'Loading...' : 'Show More' }}
          </s-button>
        </div>
      </div>

      <!-- Blog Side Panel -->
      <BlogSidePanel :is-lazy-load="isLazyLoad" :tags="tags"/>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useData } from 'vike-vue/useData';
import SButton from "#root/components/SButton.vue";
import BlogSidePanel from "#root/components/BlogSidePanel.vue";
import { getPaginatedArticles } from '/services/contentService';
import { usePageContext } from "vike-vue/usePageContext"

const pageContext = usePageContext()

const {
  articles: initialArticles,
  totalArticles,
  hasNextPage: initialHasNextPage,
  currentPage: initialPage,
  featuredArticles,
  categories,
  tags,
  error
} = useData();

const articles = ref(initialArticles);
const currentPage = ref(initialPage);
const hasNextPage = ref(initialHasNextPage);
const loading = ref(false);
const loadMoreTrigger = ref(null);
const autoLoadCount = ref(0);

const hasMorePosts = computed(() => hasNextPage.value);

const isLazyLoad = computed(() => {
  return pageContext.urlPathname === '/pulse'
})

const loadMore = async (isAutoLoad = false) => {
  if (loading.value || !hasNextPage.value) return;

  // If it's an auto-load and we've reached the limit, don't proceed
  if (isAutoLoad && autoLoadCount.value >= 2) return;

  loading.value = true;
  try {
    const nextPage = currentPage.value + 1;
    const result = await getPaginatedArticles(nextPage, 6);

    articles.value = [...articles.value, ...result.articles];
    hasNextPage.value = result.hasNextPage;
    currentPage.value = nextPage;

    // Increment auto-load counter if this was triggered automatically
    if (isAutoLoad) {
      autoLoadCount.value++;
    }
  } catch (err) {
    console.error('Error loading more articles:', err);
  } finally {
    loading.value = false;
  }
};

// Intersection Observer for infinite scroll
const observeLoadMore = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasMorePosts.value && !loading.value) {
        loadMore(true); // Pass true to indicate this is an auto-load
      }
    },
    { threshold: 0.1 }
  );

  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value);
  }

  return observer;
};

let observer;

onMounted(() => {
  observer = observeLoadMore();
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};
</script>
